import React from 'react';
import { format, differenceInDays, isToday, isPast } from 'date-fns';
import { Calendar, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import type { MatterDeadline } from '../../lib/db';
import { safeDifferenceInDays } from '../../lib/dateUtils';

interface DeadlineTimelineProps {
  deadlines: MatterDeadline[];
  onDeadlineClick?: (deadline: MatterDeadline) => void;
  maxItems?: number;
  groupByDate?: boolean;
}

export function DeadlineTimeline({
  deadlines,
  onDeadlineClick,
  maxItems = 20,
  groupByDate = true,
}: DeadlineTimelineProps) {
  // Sort deadlines by date
  const sortedDeadlines = [...deadlines]
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
    .slice(0, maxItems);

  // Group by date if requested
  const groupedDeadlines = groupByDate
    ? sortedDeadlines.reduce((groups, deadline) => {
        const dateKey = format(new Date(deadline.due_date), 'yyyy-MM-dd');
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(deadline);
        return groups;
      }, {} as Record<string, MatterDeadline[]>)
    : { all: sortedDeadlines };

  const getTimelineIcon = (deadline: MatterDeadline) => {
    if (deadline.is_completed) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    const dueDate = new Date(deadline.due_date);
    if (isPast(dueDate) && !isToday(dueDate)) {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
    if (isToday(dueDate)) {
      return <Clock className="w-5 h-5 text-orange-500" />;
    }
    return <Calendar className="w-5 h-5 text-blue-500" />;
  };

  const getDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const daysUntil = safeDifferenceInDays(date, today);

    if (isToday(date)) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil === -1) return 'Yesterday';
    if (daysUntil > 0 && daysUntil <= 7) return `In ${daysUntil} days`;
    if (daysUntil < 0 && daysUntil >= -7) return `${Math.abs(daysUntil)} days ago`;
    return format(date, 'MMM d, yyyy');
  };

  const getDateColor = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-red-600';
    if (isToday(date)) return 'text-orange-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Deadline Timeline</h3>
      </div>

      <div className="p-4">
        <div className="relative">
          {/* Timeline line */}
          {sortedDeadlines.length > 1 && (
            <div className="absolute left-6 top-8 bottom-0 w-0.5 bg-gray-200" />
          )}

          {/* Timeline items */}
          <div className="space-y-6">
            {Object.entries(groupedDeadlines).map(([dateKey, dateDeadlines]) => (
              <div key={dateKey}>
                {groupByDate && dateKey !== 'all' && (
                  <div className="flex items-center mb-3">
                    <div className="bg-white pr-3">
                      <span className={`text-sm font-medium ${getDateColor(dateDeadlines[0].due_date.toString())}`}>
                        {getDateLabel(dateDeadlines[0].due_date.toString())}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-gray-200 ml-3" />
                  </div>
                )}

                <div className="space-y-3">
                  {dateDeadlines.map((deadline, index) => (
                    <button
                      key={deadline.id}
                      onClick={() => onDeadlineClick?.(deadline)}
                      className="w-full text-left group"
                    >
                      <div className="flex items-start">
                        <div className="relative z-10 flex items-center justify-center w-12 h-12">
                          <div className="bg-white p-1 rounded-full">
                            {getTimelineIcon(deadline)}
                          </div>
                        </div>
                        
                        <div className={`
                          flex-1 ml-4 p-3 rounded-lg border transition-all
                          ${deadline.is_completed 
                            ? 'bg-gray-50 border-gray-200' 
                            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }
                        `}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className={`text-sm font-medium text-gray-900 group-hover:text-indigo-600 ${
                                deadline.is_completed ? 'line-through' : ''
                              }`}>
                                {deadline.title}
                              </h4>
                              <div className="mt-1 text-xs text-gray-500">
                                <span>{deadline.matter_name}</span>
                                {deadline.client_name && (
                                  <>
                                    <span className="mx-1">•</span>
                                    <span>{deadline.client_name}</span>
                                  </>
                                )}
                              </div>
                              {deadline.description && (
                                <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                  {deadline.description}
                                </p>
                              )}
                            </div>
                            {!groupByDate && (
                              <div className="ml-4 text-right">
                                <p className={`text-xs font-medium ${getDateColor(deadline.due_date.toString())}`}>
                                  {getDateLabel(deadline.due_date.toString())}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {format(new Date(deadline.due_date), 'h:mm a')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {sortedDeadlines.length === 0 && (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No deadlines to display</p>
            </div>
          )}

          {deadlines.length > maxItems && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Showing {maxItems} of {deadlines.length} deadlines
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}